import { useState } from "react";
import {
  motion,
  useScroll,
  useMotionValueEvent,
  useReducedMotion,
} from "framer-motion";
import { NavLink, Link } from "react-router-dom";
import { Menu, ArrowRight } from "lucide-react";

/* OP-style bar; inner row resolves strictly left → right (horizontal slide-in) */
const easeBar = [0.15, 0.85, 0.32, 1];
const easeItems = [0.16, 1, 0.3, 1];

function Header({ toggle, menu }) {
  const reduceMotion = useReducedMotion();
  const [elevated, setElevated] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (y) => {
    setElevated(y > 8);
  });

  const barHidden = reduceMotion ? false : { y: "-100%" };
  const barShown = { y: 0 };

  /* Slide in from the left, one slot at a time across the bar */
  const innerHidden = reduceMotion
    ? { opacity: 1, x: 0, y: 0 }
    : { opacity: 0, x: -22, y: 0 };
  const innerShown = { opacity: 1, x: 0, y: 0 };

  const barDuration = reduceMotion ? 0 : 0.76;
  const innerDuration = reduceMotion ? 0 : 0.55;
  const t0 = reduceMotion ? 0 : 0.38;
  const step = reduceMotion ? 0 : 0.076;

  const delayLtr = (index) => t0 + index * step;

  const linkClass = ({ isActive }) =>
    [
      "relative inline-flex items-center whitespace-nowrap border-b-2 py-1 text-[11px] font-semibold uppercase leading-none tracking-[0.14em] transition-colors duration-200",
      isActive
        ? "border-burnt-sienna-400 text-white"
        : "border-transparent text-white/60 hover:text-white",
    ].join(" ");

  return (
    <motion.header
      className="sticky top-0 z-50 border-b border-white/[0.08] text-white backdrop-blur-md will-change-transform"
      initial={barHidden}
      animate={{
        ...barShown,
        backgroundColor: elevated
          ? "rgba(13, 31, 40, 0.98)"
          : "rgba(13, 31, 40, 0.92)",
        boxShadow: elevated
          ? "0 12px 40px -12px rgba(0, 0, 0, 0.45)"
          : "0 1px 0 0 rgba(255, 255, 255, 0.06)",
      }}
      transition={{
        y: { duration: barDuration, ease: easeBar },
        backgroundColor: {
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1],
        },
        boxShadow: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
      }}
    >
      {/* Bottom accent — subtle teal line above hero (reference layout) */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-complement-500/35 to-transparent"
        aria-hidden
      />

      <div className="relative mx-auto grid h-[60px] max-w-[1280px] grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 px-5 sm:px-6 lg:h-[72px] lg:grid-cols-[auto_1fr] lg:gap-x-8 lg:px-10">
        {/* Logo + mobile menu — reveals first (OP-style sequence) */}
        <motion.div
          className="flex min-w-0 items-center gap-2 sm:gap-3"
          initial={innerHidden}
          animate={innerShown}
          transition={{
            duration: innerDuration,
            ease: easeItems,
            delay: delayLtr(0),
          }}
        >
          <button
            type="button"
            className="-ml-1 shrink-0 rounded-md p-2 text-white/85 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
            onClick={toggle}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>
          <NavLink
            to="/"
            className="flex shrink-0 items-center transition-opacity hover:opacity-90"
          >
            <img
              src="/assets/images/logo.svg"
              alt="Velozent"
              className="h-[26px] w-auto sm:h-7 lg:h-[30px] contrast-[1.03] [transform:translateZ(0)] [backface-visibility:hidden]"
              decoding="async"
            />
          </NavLink>
        </motion.div>

        {/* Desktop: nav + CTA — left-to-right stagger */}
        <nav className="hidden min-h-0 w-full items-center justify-end lg:flex">
          <div className="flex items-center gap-7 xl:gap-9">
            <ul className="flex items-center gap-7 xl:gap-9">
              {menu.map((item, i) => (
                <motion.li
                  key={item.id}
                  className="flex items-center"
                  initial={innerHidden}
                  animate={innerShown}
                  transition={{
                    duration: innerDuration,
                    ease: easeItems,
                    delay: delayLtr(1 + i),
                  }}
                >
                  <NavLink to={item.path} className={linkClass}>
                    {item.name}
                  </NavLink>
                </motion.li>
              ))}
            </ul>

            <motion.span
              className="hidden h-[18px] w-px shrink-0 bg-white/20 sm:block"
              aria-hidden
              initial={innerHidden}
              animate={innerShown}
              transition={{
                duration: innerDuration,
                ease: easeItems,
                delay: delayLtr(1 + menu.length),
              }}
            />

            <motion.div
              initial={innerHidden}
              animate={innerShown}
              transition={{
                duration: innerDuration,
                ease: easeItems,
                delay: delayLtr(2 + menu.length),
              }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center gap-1.5 whitespace-nowrap py-1 text-[11px] font-semibold uppercase leading-none tracking-[0.14em] text-white transition-colors hover:text-burnt-sienna-200"
              >
                Get in touch
                <ArrowRight
                  className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
                  strokeWidth={2}
                />
              </Link>
            </motion.div>
          </div>
        </nav>

        {/* Mobile: right column — second beat after logo (left → right) */}
        <motion.div
          className="justify-self-end lg:hidden"
          initial={innerHidden}
          animate={innerShown}
          transition={{
            duration: innerDuration,
            ease: easeItems,
            delay: delayLtr(1),
          }}
        >
          <Link
            to="/contact"
            className="text-[10px] font-semibold uppercase leading-none tracking-[0.14em] text-burnt-sienna-200/95 transition-colors hover:text-burnt-sienna-100"
          >
            Contact
          </Link>
        </motion.div>
      </div>
    </motion.header>
  );
}

export default Header;
